/** Versioned progress document: history union/dedupe, PB derivation, deterministic settings merge. */
import { MIN_REACTION_MS, PROGRESS_MAX_BYTES, type Difficulty, type Mode } from './config';
import type { RoundResult } from './types';

export interface ProgressDoc { v: 1; history: RoundResult[]; settings: Record<string, unknown>; settingsAt: number }
export const emptyDoc = (): ProgressDoc => ({ v: 1, history: [], settings: {}, settingsAt: 0 });

export function unionHistory(a: RoundResult[], b: RoundResult[]): RoundResult[] {
  const map = new Map<string, RoundResult>();
  for (const e of [...a, ...b]) if (e && typeof e.id === 'string' && !map.has(e.id)) map.set(e.id, e);
  return [...map.values()].sort((x, y) => x.at - y.at || (x.id < y.id ? -1 : 1));
}

/** Deterministic: newer settingsAt wins; ties resolve to the lexicographically larger JSON so both sides agree. */
export function mergeDocs(local: ProgressDoc, remote: ProgressDoc): ProgressDoc {
  let settings: Record<string, unknown>; let settingsAt: number;
  if (local.settingsAt !== remote.settingsAt) ({ settings, settingsAt } = local.settingsAt > remote.settingsAt ? local : remote);
  else { const a = JSON.stringify(local.settings), b = JSON.stringify(remote.settings); settings = a >= b ? local.settings : remote.settings; settingsAt = local.settingsAt; }
  return { v: 1, history: unionHistory(remote.history, local.history), settings, settingsAt };
}

/** Trim oldest history until the document fits the byte cap. */
export function fitDoc(doc: ProgressDoc): ProgressDoc {
  const enc = new TextEncoder();
  let d = doc;
  while (enc.encode(JSON.stringify(d)).length > PROGRESS_MAX_BYTES && d.history.length) d = { ...d, history: d.history.slice(Math.ceil(d.history.length * 0.1)) };
  return d;
}

export const pbKey = (mode: Mode, diff: Difficulty, pvp: boolean) => `${mode}:${diff}:${pvp ? 'pvp' : 'solo'}`;
export function isPbEligible(e: RoundResult): boolean {
  return e.ok && !e.assisted && e.reactionMs != null && e.reactionMs >= MIN_REACTION_MS && Number.isFinite(e.reactionMs);
}
export function personalBests(history: RoundResult[]): Record<string, RoundResult> {
  const pbs: Record<string, RoundResult> = {};
  for (const e of history) {
    if (!isPbEligible(e)) continue;
    const k = pbKey(e.mode, e.difficulty, e.pvp);
    if (!pbs[k] || e.reactionMs! < pbs[k]!.reactionMs!) pbs[k] = e;
  }
  return pbs;
}

export function toCsv(history: RoundResult[]): string {
  const cols = ['id', 'at', 'mode', 'difficulty', 'pvp', 'ok', 'verdict', 'reason', 'reactionMs', 'reaction2Ms', 'landMs', 'entryMcUsd', 'feesSol', 'pnlSol', 'assisted', 'multiplayer'] as const;
  const esc = (v: unknown) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [cols.join(','), ...history.map((e) => cols.map((c) => esc(c === 'at' ? new Date(e.at).toISOString() : e[c])).join(','))].join('\n');
}
